import { UniqueIdentifier } from '@dnd-kit/core';
import { FlashcardType } from '@osmosis/shared/quiz';
import { Tag } from '@osmosis/shared/types/learn';

import { LocalQuizSettings } from './page';
import { SpacedRepetitionState } from './queues';

type StringWithNode = {
  s: string;
  a: string[] | string; // NodeID of the image
};

type StringBoolean = 'true' | 'false';
export type NodeString = StringWithNode | string;

export enum AnswerOrder {
  Randomized,
  RandomizedExceptLast,
  RandomizedExceptLast2,
  RandomizedExceptLast3,
  AsListed,
}

export enum XpTypeMap {
  CORRECT = 0, // Correct answer type - awarded when users get MC questions correct
  EXTRA = 1,
} // Explanation answer type - awarded when users spend 1 minutes reading explanations for MC questions

export type XpLog = {
  type: XpTypeMap;
  data: {
    nodeID: number;
  };
};

export type LineItem = {
  text: string;
  xp: number;
};

export type ReferenceProps = {
  org: string;
  t: string;
  y: string;
  _: string;
};

export type QuizExplanationObject = {
  a: string;
  s: string;
};

export type QuizAnswer = {
  explanation: string | QuizExplanationObject;
  text: string;
  isCorrect: boolean;
};

export type HotSpotAnswer = {
  area: [{ x: string; y: string }]; // area
  text: string; // answer text
};

export type EnhancedHotSpotNodeAnswer = {
  start: string;
  end: string;
  text: string;
};
export type EnhancedHotSpotAnswer = {
  startOffset: number;
  endOffset: number;
  text: string;
}; // area;

export type OrderResponseAnswer = {
  text: string; // answer text
  correctValue: number; //answer correct number index
  explanation: string; // explanation
};

export interface OptionTextResponseAnswer {
  type: 'text';
  text: string;
}

export interface OptionSelectResponseAnswer {
  type: 'select';
  answer: string;
  options: string[];
  index: number;
}

export type OptionsResponseAnswer =
  | OptionTextResponseAnswer
  | OptionSelectResponseAnswer;

export type QuizAnswerType =
  | QuizAnswer
  | HotSpotAnswer
  | OrderResponseAnswer
  | EnhancedHotSpotAnswer
  | OptionsResponseAnswer;

export type MatrixAnswer = {
  columns: { name: string }[];
  rows: { name: string; correctValue: string }[];
  optionsHeader: string;
};

export type MultipleChoiceData = {
  answers: QuizAnswer[];
  question: string;
  answerOrder: AnswerOrder;
  mainExplanation: string;
  keyTakeaway: string;
  loggedOut?: string;
};

export type FlashcardData = {
  front: string;
  mainExplanation?: string; // back of flashcard
  flashcardType?: FlashcardType;
  loggedOut?: string;
};

export type TrueFalseData = {
  question: string;
  mainExplanation?: string; // back of flashcard
  answers: QuizAnswer[];
  loggedOut?: string;
};

export type GraphicData = {
  question: string;
  mainExplanation?: string;
  keyTakeaway?: string;
  answers: QuizAnswer[];
  answerOrder: AnswerOrder;
  loggedOut?: string;
};

export type OrderedResponseData = {
  question: string; // question text
  mainExplanation: string; // main explanation
  answerOrder: number;
  answers: OrderResponseAnswer[]; // Answers
  keyTakeaway?: string;
  loggedOut?: string;
};

export type MatrixData = {
  question: string;
  keyTakeaway: string;
  answers: MatrixAnswer;
  loggedOut?: string;
};

export type FillInTheBlanksData = {
  question: string;
  answer: string;
  image?: string;
  units?: string;
  keyTakeaways?: FillInTheBlankKeytakeawayItem[];
  keyTakeaway?: string;
  loggedOut?: string;
};

export type DragAndDropData = {
  question: string;
  answers: QuizAnswer[];
  shuffledAnswers: QuizAnswer[];
  mainExplanation: string;
  keyTakeaway: string;
  optionsText: string;
  answersText: string;
  ordered: boolean;
  loggedOut?: string;
};

export type ClozeResponseData = {
  question: string; // question text
  mainExplanation: string; // main explanation
  answers: OptionsResponseAnswer[]; // answers
  loggedOut?: string;
};

export type SentenceDragAndDropData = {
  question: string;
  answers: SentenceDragAndDropNodeData['a'];
  mainExplanation: string;
  distractors: SentenceDragAndDropNodeData['distractors'];
  keyTakeaway?: string;
  loggedOut?: string;
};

export type QuizData =
  | FillInTheBlanksData
  | MultipleChoiceData
  | FlashcardData
  | TrueFalseData
  | DragAndDropData
  | HotSpotData
  | GraphicData
  | EnhancedHotSpotData
  | ClozeResponseData
  | MatrixData
  | SentenceDragAndDropData
  | OrderedResponseData
  | OrderedResponseData; // | OtherQuizTypeData, etc

export type QuizHistory = {
  correctN: number;
  lastAccuracy: QuizAccuracy;
  lastConfidence: QuizConfidence;
  timestamp: string;
};

export type QuizItem<T = QuizData> = {
  subtype: number;
  userID: number;
  nodeID: number;
  data: T;
  history?: QuizHistory;
};

export type QuizItemsReq = {
  nodeIDs?: number[];
  selectAll?: number;
  set?: string;
};

export type QuizFilterState = {
  remaining: boolean;
  correctHigh: boolean;
  correctMedium: boolean;
  correctLow: boolean;
  incorrectHigh: boolean;
  incorrectMedium: boolean;
  incorrectLow: boolean;
};

export type FlashcardQuizItem = QuizItem<FlashcardData>;

export type QuizProps = {
  items: QuizItem[];
  quizType: QuizTypes;
  bundleID?: string;
  isDailyPractice?: boolean;
  showQuizStart?: boolean;
  quizSettings?: LocalQuizSettings;
  queueType?: string;
  relevantVideos?: QuizRecommendedConcept[];
  revalidateData?: () => void;
  learnName: string;
};

export type QuizResponseItemProps = {
  item: QuizItem;
  rating?: 1 | -1;
  response?: QuizResponse;
  nodeID?: number;
};

export type QuizResponse = {
  answer?: ItemResponseType;
  accuracy: boolean;
  confidence: QuizConfidence;
  timestamp: number;
};

export type RepetitionStats = {
  due: {
    userID?: number;
    nodeID?: number;
    eFactor?: number;
    interval?: number;
    flaggedOn?: Date;
    repeatOn?: Date;
    state?: number;
    MID?: number;
  };
  intervals: number[];
};

export enum QuizAccuracy {
  INCORRECT,
  CORRECT,
}

export enum QuizConfidence {
  NA,
  LOW,
  MEDIUM,
  HIGH,
}

export type OrderedResponseSubmittedAnswerType = Record<number, number>;
export type MultipleChoiceSubmittedAnswerType = number;
export type MultipleCheckboxSubmittedAnswerType = number[];
export type FillInTheBlankSubmittedAnswerType = string;
export type SentenceDragAndDropSubmittedAnswerType = {
  [key: string]: UniqueIdentifier | null;
};

export type DragAndDropSubmittedAnswerType = string[];
export type ItemResponseType =
  | OrderedResponseSubmittedAnswerType
  | MultipleChoiceSubmittedAnswerType
  | SentenceDragAndDropSubmittedAnswerType
  | DragAndDropSubmittedAnswerType
  | MultipleCheckboxSubmittedAnswerType
  | FillInTheBlankSubmittedAnswerType; // | OtherQuizTypeData, etc

type CommonNodeData = {
  q: NodeString; // question text
  e?: NodeString; // main explanation
};

export type MultipleChoiceNodeData = CommonNodeData & {
  loggedOut: string; // loggedOut questions flag
  kp: string; // key takeaway
  ao: string; // answer order, string of number
  // Answers
  a: {
    e: string; // explanation
    v: string; // answer text
    c: string; // is correct answer
  }[];
};
export type FlashcardNodeData = CommonNodeData & {
  z: FlashcardType;
};
export type TrueFalseNodeData = CommonNodeData & {
  a: 't' | 'f';
};

export type GraphicNodeData = {
  q: string | StringWithNode; // question text
  e?: string | StringWithNode; // main explanation
  ao: string; // answer order, string of number
  kp: CommonNodeData['e'];
  // Answers
  a: {
    e: string; // explanation
    v: { s: string; a: string }; // answer text
    c: string; // is correct answer
  }[];
};

export type OrderedResponseNodeData = {
  q: string; // question text
  e?: { s: string; a: string }; // main explanation
  // Answers
  a: {
    name: string; // answer text
    correctValue: string; //answer correct value string<number>
    exp: string; // explanation
  }[];
  kp?: CommonNodeData['e'];
};

export type DragAndDropNodeData = CommonNodeData & {
  a: { v: CommonNodeData['q']; r: StringBoolean; exp: CommonNodeData['e'] }[];
  optionsText: string;
  answersText: string;
  ordered: StringBoolean;
  kp: CommonNodeData['e'];
  // not full (check _ variants)
  r?: ReferenceProps[];
};
export type SentenceDragAndDropNodeData = CommonNodeData & {
  a: { type: 'text' | 'answer'; value: string }[];
  kp: CommonNodeData['e'];
  distractors: string[];
};

export type FillInTheBlankKeytakeaway = {
  title: string;
  takeaway: string;
};

export type FillInTheBlankKeytakeawayItem = {
  title: string;
  takeaway: {
    s: string;
    a: string;
  };
};

export type FillInTheBlankNodeData = {
  q: string;
  a: string;
  image?: string;
  u?: string;
  e?: CommonNodeData['e'];
  kp?: string;
  kps?: FillInTheBlankKeytakeawayItem[];
};

export type MatrixNodeData = {
  q: string;
  a: MatrixAnswer;
  kp: { s: string };
};

export type HotSpotData = {
  question: string; // question (png)
  directions: string; // direction
  loggedOut?: string;
  // Answers
  answers: HotSpotAnswer;
  mainExplanation?: string;
  keyTakeaway?: string;
};

export type HotSpotNodeData = CommonNodeData & {
  q: string; // question (png)
  directions: NodeString; // direction
  e: NodeString; // main explanation
  // Answers
  a: {
    area: [{ x: string; y: string }]; // area
    text: string; // answer text
  }[];
  r?: ReferenceProps[];
  kp: NodeString;
};

export type AnswerHotSpotType = EnhancedHotSpotAnswer & {
  type?: string;
  id?: number;
};

export type EnhancedHotSpotNodeData = CommonNodeData & {
  directions: string | { s: string; a?: string }; // direction
  a: EnhancedHotSpotNodeAnswer[]; // Answers
};

export type EnhancedHotSpotData = {
  question: string; // question
  directions: string | { s: string; a?: string }; // direction
  mainExplanation: string; // main explanation
  answers: EnhancedHotSpotAnswer[]; // Answers
  loggedOut?: string;
};

export type ClozeResponseNodeData = {
  e?: NodeString; // main explanation
  q: NodeString; // question text
  r?: {
    _: string;
    t: string;
    y: string;
    org: string;
  }[];
};

export type NodeData =
  | FillInTheBlankNodeData
  | MultipleChoiceNodeData
  | FlashcardNodeData
  | TrueFalseNodeData
  | DragAndDropNodeData
  | HotSpotNodeData
  | GraphicNodeData
  | DragAndDropNodeData
  | EnhancedHotSpotNodeData
  | ClozeResponseNodeData
  | MatrixNodeData
  | SentenceDragAndDropNodeData
  | OrderedResponseNodeData
  | OrderedResponseNodeData;

export type MultipleChoiceMediaData = {
  id: number;
  t: string;
  $: string;
  u: string;
};

export type HotSpotMediaData = {
  id: number;
  t: string;
  $: string;
  u: string;
};

export type EnhancedHotSpotMediaData = {
  id: number;
  t: string;
  $: string;
  u: string;
};

export type LabelMatchingMediaData = {
  $: string;
  s: string;
};

export type FlashcardMediaData = {
  v: string;
  t: string;
  by: string;
  ds: string;
  dr: string;
  vc: string;
  $: string;
};

export type GraphicMediaData = {
  id: number;
  t: string;
  $: string;
  u: string;
};

export type TableFlashcardMediaData = {
  $: string;
};

export type ClozeMediaData = {
  id: number;
  t: string;
  $: string;
  u: string;
};

export type NumberMediaData = { n: number };

export type NodeMediaData = (
  | MultipleChoiceMediaData
  | LabelMatchingMediaData
  | FlashcardMediaData
  | TableFlashcardMediaData
  | GraphicMediaData
  | NumberMediaData
  | HotSpotMediaData
  | ClozeMediaData
)[];

export type NodeRes = {
  data: NodeData[];
  nodeID: number[];
  subtype: number[];
  userID: number[];
};

export type QuizHistoryData = {
  correctN: number[];
  lastAccuracy: number[];
  lastConfidence: number[];
  nodeID: number[];
  timestamp: string[];
};

/**
 * Results from an endpoint, contains data as stored in the DB.
 */
export type BaseQuizRes<T = NodeMediaData> = {
  historyData: QuizHistoryData;
  mediaData: Omit<NodeRes, 'data'> & { data: T };
  nodeData: NodeRes;
  opinionData: Record<string, unknown>;
};

export type MediaData<T = NodeMediaData> = BaseQuizRes<T>['mediaData'];

export enum QuizItemTime {
  NA = 'NA',
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  FOUR_DAYS_AGO = 'FOUR_DAYS_AGO',
  ONE_WEEK_AGO = 'ONE_WEEK_AGO',
  TWO_WEEKS_AGO = 'TWO_WEEKS_AGO',
  THREE_WEEKS_AGO = 'THREE_WEEKS_AGO',
  FOUR_WEEKS_AGO = 'FOUR_WEEKS_AGO',
  EIGHT_WEEKS_AGO = 'EIGHT_WEEKS_AGO',
  TWO_MONTHS_AGO = 'TWO_MONTHS_AGO',
}

export type QuizChartDataSlice = {
  id: string;
  name: number;
  value: number;
  color: string;
  text: string;
  items: QuizProgressItem[];
  index: number;
};

export type QuizInnerChartDataSlice = QuizChartDataSlice & {
  parentName: number;
};

export type QuizProgressItem = {
  item: QuizItem;
  answered?: boolean;
  accuracy?: QuizAccuracy;
  confidence?: QuizConfidence;
  time?: string | number;
};

export type QuizChartDefaultOuterData = {
  name: number;
  value: number;
  color: string;
};

export enum QuizTypes {
  Flashcards = 'flashcards',
  Questions = 'questions',
}

type SharedResponseDue = {
  eFactor: number;
  nodeID: number;
  interval: number;
  flaggedOn: string;
  repeatOn: string;
  state: SpacedRepetitionState;
};

export type ResponseDueRow = SharedResponseDue & {
  userID: number;
};

export type ResponseDueData = SharedResponseDue & {
  flagged: boolean;
};

export type ItemHistory = {
  ac: 0 | 1;
  ts: number;
};

export type QuizRecommendedConcept = {
  pageName: string;
  duration: string;
  url: string;
  learnNodeID: number;
  tags: Tag[];
  smallThumb?: string;
};
